/**
 * Model Health Service — wraps /api/admin/ai/model-health/ endpoints
 * Phase 4.8-B — WQ-200
 */

// === Types ===

export interface HealthSnapshot {
  id: string;
  model_id: string;
  deployment_id: string | null;
  tenant_id: string;
  snapshot_type: string;
  prediction_count_24h: number;
  prediction_count_7d: number;
  error_rate_24h: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  accuracy_current: number | null;
  accuracy_baseline: number | null;
  accuracy_delta: number | null;
  data_quality_score: number | null;
  input_drift_detected: boolean;
  concept_drift_detected: boolean;
  overall_health: string;
  health_details: Record<string, unknown> | null;
  created_at: string;
}

export interface DriftEvent {
  id: string;
  model_id: string;
  tenant_id: string;
  drift_type: string;
  severity: string;
  details: Record<string, unknown> | null;
  snapshot_id: string | null;
  is_resolved: boolean;
  resolved_at: string | null;
  detected_at: string;
  created_at: string;
}

export interface DashboardItem {
  model_id: string;
  deployment_id: string | null;
  overall_health: string;
  prediction_count_24h: number;
  accuracy_current: number | null;
  accuracy_delta: number | null;
  data_quality_score: number | null;
  input_drift_detected: boolean;
  concept_drift_detected: boolean;
  unresolved_drift_events: number;
  last_snapshot_at: string | null;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function takeSnapshot(
  authFetch: FetchFn,
  modelId: string,
): Promise<HealthSnapshot> {
  const res = await authFetch(`/api/admin/ai/model-health/snapshot/${modelId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to take health snapshot: ${res.status}`);
  return res.json();
}

export async function listSnapshots(
  authFetch: FetchFn,
  params?: { model_id?: string; limit?: number; offset?: number },
): Promise<HealthSnapshot[]> {
  const qs = new URLSearchParams();
  if (params?.model_id) qs.set('model_id', params.model_id);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/model-health/snapshots${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list snapshots: ${res.status}`);
  return res.json();
}

export async function getSnapshot(
  authFetch: FetchFn,
  snapshotId: string,
): Promise<HealthSnapshot> {
  const res = await authFetch(`/api/admin/ai/model-health/snapshots/${snapshotId}`);
  if (!res.ok) throw new Error(`Failed to get snapshot: ${res.status}`);
  return res.json();
}

export async function getLatestSnapshot(
  authFetch: FetchFn,
  modelId: string,
): Promise<HealthSnapshot> {
  const res = await authFetch(`/api/admin/ai/model-health/snapshots/latest/${modelId}`);
  if (!res.ok) throw new Error(`Failed to get latest snapshot: ${res.status}`);
  return res.json();
}

export async function getDashboard(authFetch: FetchFn): Promise<DashboardItem[]> {
  const res = await authFetch('/api/admin/ai/model-health/dashboard');
  if (!res.ok) throw new Error(`Failed to get health dashboard: ${res.status}`);
  return res.json();
}

export async function runDriftCheck(
  authFetch: FetchFn,
  modelId: string,
): Promise<HealthSnapshot> {
  const res = await authFetch(`/api/admin/ai/model-health/drift-check/${modelId}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to run drift check: ${res.status}`);
  return res.json();
}

export async function listDriftEvents(
  authFetch: FetchFn,
  params?: { model_id?: string; severity?: string; is_resolved?: boolean; limit?: number },
): Promise<DriftEvent[]> {
  const qs = new URLSearchParams();
  if (params?.model_id) qs.set('model_id', params.model_id);
  if (params?.severity) qs.set('severity', params.severity);
  if (params?.is_resolved !== undefined) qs.set('is_resolved', String(params.is_resolved));
  if (params?.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await authFetch(`/api/admin/ai/model-health/drift-events${query ? '?' + query : ''}`);
  if (!res.ok) throw new Error(`Failed to list drift events: ${res.status}`);
  return res.json();
}

export async function resolveDriftEvent(
  authFetch: FetchFn,
  eventId: string,
): Promise<DriftEvent> {
  const res = await authFetch(`/api/admin/ai/model-health/drift-events/${eventId}/resolve`, {
    method: 'PATCH',
  });
  if (!res.ok) throw new Error(`Failed to resolve drift event: ${res.status}`);
  return res.json();
}
